import type { AxiosError, AxiosResponse } from "axios";
import type {
  AddTeamMemberSkillItem,
  RemoveTeamMemberSkillsResult,
  SkillProficiencyValue,
  TeamMemberSkillCategoryEntry,
} from "shared";
import { axiosClient } from "utilz";
import { apiErrorHandler, fromApiProficiency } from "utilz/helpers";

/**
 * Endpoints #11–14 from the team-member API reference. All routes are
 * scoped to a single resource (`/v1/control-center/resource/:resource_id/...`).
 * Successful envelope: `{ success: true, statusCode: 200, data: ... }`.
 *
 * All request/response DTO types live in `shared/types/teamMembers.ts`.
 */

const isOk = (
  responseData: { success?: boolean; statusCode?: number } | null | undefined,
) => responseData?.success === true && responseData?.statusCode === 200;

/**
 * GET /v1/control-center/resource/:resource_id/skills
 *
 * Response is grouped by category, and each skill carries its provenance:
 * `source`, `is_manual_override` and the full `sources[]` contributor list.
 * `proficiency` is the value backend **resolved** from those contributors
 * (manual wins, else highest inherited) using the same code AI routing reads —
 * never re-derive it downstream.
 */
export const getTeamMemberSkills = async (
  resourceId: string,
): Promise<TeamMemberSkillCategoryEntry[]> => {
  try {
    const url = `/v1/control-center/resource/${resourceId}/skills`;
    const response = await axiosClient.get(url);
    const responseData = response?.data;
    if (isOk(responseData) && Array.isArray(responseData.data)) {
      // A category with no skills isn't emitted at all, so `skills` is always
      // an array — same assumption `useTeamMemberSkills` already makes.
      return (responseData.data as TeamMemberSkillCategoryEntry[]).map(
        (category) => ({
          ...category,
          skills: category.skills.map((skill) => ({
            ...skill,
            proficiency: fromApiProficiency(skill.proficiency),
            // `sources[].proficiency` carries the SAME mixed casing as the
            // leaf — this response is precisely where the two casings meet,
            // inherited rows capitalised and manual rows lowercase in one
            // array. Normalising only the leaf would leave the multi-source
            // tooltip rendering "Expert" next to "expert" and any comparison
            // against it silently false.
            sources: skill.sources?.map((source) => ({
              ...source,
              proficiency: fromApiProficiency(source.proficiency),
            })),
          })),
        }),
      );
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
    return [];
  }
};

/** POST /v1/control-center/resource/:resource_id/skills */
export const addTeamMemberSkills = async (
  resourceId: string,
  skills: AddTeamMemberSkillItem[],
) => {
  try {
    const url = `/v1/control-center/resource/${resourceId}/skills`;
    const response = await axiosClient.post(url, { skills });
    const responseData = response?.data;
    if (isOk(responseData)) {
      return responseData.data;
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

/**
 * PATCH /v1/control-center/resource/:resource_id/skills/:resource_skill_id
 *
 * Updates only the proficiency level for an assigned skill — the skill
 * itself cannot be swapped, the caller must DELETE then POST instead.
 */
export const updateTeamMemberSkillProficiency = async (
  resourceId: string,
  resourceSkillId: string,
  proficiency: SkillProficiencyValue,
) => {
  try {
    const url = `/v1/control-center/resource/${resourceId}/skills/${resourceSkillId}`;
    const response = await axiosClient.patch(url, { proficiency });
    const responseData = response?.data;
    if (isOk(responseData)) {
      return responseData.data;
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

/**
 * DELETE /v1/control-center/resource/:resource_id/skills
 *
 * ⚠️ Two outcomes, not one. Removing a **manual override** on an inherited
 * skill doesn't delete the row — it drops the override and the skill reverts to
 * its inherited value, coming back in `reverted_to_inherited`. Only rows
 * counted by `deleted_count` actually left the member.
 *
 * Removing an **inherited-only** skill is rejected with a `400` naming the
 * profiles that grant it; callers must not offer the action (the tab filters
 * those ids out before they reach here).
 */
export const removeTeamMemberSkills = async (
  resourceId: string,
  resourceSkillIds: string[],
): Promise<RemoveTeamMemberSkillsResult | undefined> => {
  try {
    const url = `/v1/control-center/resource/${resourceId}/skills`;
    const response = await axiosClient.delete(url, {
      data: { resource_skill_ids: resourceSkillIds },
    });
    const responseData = response?.data;
    if (isOk(responseData)) {
      const raw = (responseData.data ?? responseData) as {
        deleted_count?: number | string | null;
        reverted_to_inherited?: unknown;
      };
      const reverted = Array.isArray(raw.reverted_to_inherited)
        ? raw.reverted_to_inherited.filter(
            (id): id is string => typeof id === "string",
          )
        : [];
      return {
        // Fall back to "everything we asked for, minus the reverts" so an
        // environment that predates the field still reports a sane count
        // rather than claiming nothing was deleted.
        deletedCount: Number(
          raw.deleted_count ?? resourceSkillIds.length - reverted.length,
        ),
        revertedToInherited: reverted,
      };
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};
